import type { z } from 'zod';
import { MOTION_PLAN_VERSION } from '../animation/procedural/spec';
import * as A from '../domain/animationOps';
import * as av from '../domain/avOps';
import { applyMotionPlan } from '../domain/motionOps';
import * as S from '../domain/sceneOps';
import {
  CURRENT_SCHEMA_VERSION,
  ProjectSchema,
  SCENE_REACTIONS,
  formatIssues,
  type FillGradient,
  type Glow,
  type Grade,
  type Project,
  type Scene,
  type SceneObject,
  type Shadow,
  type Vignette,
} from '../domain/schemas';
import * as viz from '../domain/visualizerOps';
import { withIdFactory } from '../lib/ids';
import { NEUTRAL_GRADE } from '../scene/look';
import { buildPresetScene } from '../scene/presets';
import { contextRevision } from './context';
import {
  DESTRUCTIVE_OPERATIONS,
  OPERATION_NAMES,
  PACKAGE_FORMAT,
  PACKAGE_LIMITS,
  PackageSchema,
  SUPPORTED_PACKAGE_VERSIONS,
  version2FieldsIn,
  type AppearanceSpec,
  type CreationPackage,
  type FillGradientSpec,
  type GlowSpec,
  type GradeSpec,
  type LookSpec,
  type ShadowSpec,
  type VignetteSpec,
  type ObjectPatchSpec,
  type ObjectSpec,
  type Operation,
  type Ref,
} from './package';

/**
 * Applies an assistant creation package to a *staged* copy of the project (Phase 5A).
 *
 * Every operation is carried out by the existing domain operations, on the project value in
 * memory (they are pure), so the live project is never touched. The result is either a
 * fully validated staged project plus a preview, or precise errors and no changes at all.
 * Committing goes through `commitStaged`, which the Assistant workspace runs as one
 * undoable edit after the owner confirms.
 */

export type StageFailureKind = 'size' | 'json' | 'format' | 'version' | 'schema' | 'compatibility' | 'conflict' | 'already-applied' | 'operation' | 'validation';

export interface BridgeIssue {
  /** Where in the package (e.g. "operations[3].keys[0].time"); empty for the whole file. */
  path: string;
  message: string;
}

export type EntityKind = 'scene' | 'layer' | 'object' | 'character' | 'animation' | 'session' | 'reaction' | 'visualizer layer';

export interface EntityChange {
  kind: EntityKind;
  id: string;
  name: string;
  /** The scene an object or layer belongs to. */
  sceneName?: string;
}

export interface OperationSummary {
  index: number;
  op: Operation['op'];
  description: string;
}

export interface StagedPackage {
  pkg: CreationPackage;
  /** The project this was staged against. */
  base: Project;
  /** `contextRevision(base)`: applying requires the creative content to be unchanged. */
  baseRevision: string;
  next: Project;
  copy: number;
  operations: OperationSummary[];
  created: EntityChange[];
  modified: EntityChange[];
  deleted: EntityChange[];
  keyframesAdded: number;
  warnings: string[];
  /** Non-empty when applying removes or replaces existing content: needs explicit confirmation. */
  destructive: string[];
  /** Scene to show after applying. */
  focusSceneId: string | null;
}

export type StageResult = { ok: true; staged: StagedPackage } | { ok: false; kind: StageFailureKind; errors: BridgeIssue[] };

export interface StageOptions {
  /** 0 = the package's own ids; n > 0 = an explicit, numbered copy with fresh ids. */
  copy?: number;
}

class PackageOpError extends Error {
  constructor(
    readonly path: string,
    message: string,
  ) {
    super(message);
  }
}

class AlreadyApplied extends Error {}

const fail = (kind: StageFailureKind, errors: BridgeIssue[]): StageResult => ({ ok: false, kind, errors });

const zodPath = (path: readonly PropertyKey[]) =>
  path.reduce<string>((s, p) => (typeof p === 'number' ? `${s}[${p}]` : s ? `${s}.${String(p)}` : String(p)), '');

const zodIssues = (error: z.ZodError): BridgeIssue[] => error.issues.slice(0, 20).map((i) => ({ path: zodPath(i.path), message: i.message }));

/** 24 hex characters derived from a string (three FNV-1a passes; not cryptographic). */
function hash24(text: string): string {
  let out = '';
  for (const salt of ['a', 'b', 'c']) {
    let h = 0x811c9dc5;
    const s = `${salt}|${text}`;
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    out += h.toString(16).padStart(8, '0');
  }
  return out;
}

/** Every `id` value in a project (used to detect a package that was already applied). */
function allIds(value: unknown, out = new Set<string>()): Set<string> {
  if (Array.isArray(value)) for (const v of value) allIds(v, out);
  else if (value && typeof value === 'object') {
    for (const [k, v] of Object.entries(value)) {
      if (k === 'id' && typeof v === 'string') out.add(v);
      else if (typeof v === 'object') allIds(v, out);
    }
  }
  return out;
}

const VISUAL_FIELDS = new Set(['scenes', 'activeSceneId', 'characters', 'animations', 'avSessions']);

/** Parses, validates and stages a package. Never modifies `project`. */
export function stagePackage(project: Project, text: string, options: StageOptions = {}): StageResult {
  // 1. Size and JSON.
  const bytes = new TextEncoder().encode(text).length;
  if (bytes > PACKAGE_LIMITS.bytes) {
    return fail('size', [{ path: '', message: `The package is ${(bytes / 1024).toFixed(0)} KB; the limit is ${PACKAGE_LIMITS.bytes / 1024} KB.` }]);
  }
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (error) {
    return fail('json', [{ path: '', message: `This is not valid JSON (${error instanceof Error ? error.message : 'parse error'}).` }]);
  }
  // 2. Format and version, with clear messages before the full schema.
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return fail('format', [{ path: '', message: 'A creation package is a JSON object.' }]);
  const head = raw as Record<string, unknown>;
  if (head.format !== PACKAGE_FORMAT) {
    return fail('format', [{ path: 'format', message: `Expected "format": "${PACKAGE_FORMAT}". This file is not a SonicForge creation package.` }]);
  }
  if (!SUPPORTED_PACKAGE_VERSIONS.includes(head.version as 1)) {
    return fail('version', [{ path: 'version', message: `Package version ${JSON.stringify(head.version)} is not supported. This SonicForge reads version ${SUPPORTED_PACKAGE_VERSIONS.join(', ')}.` }]);
  }
  if (Array.isArray(head.operations)) {
    const unknown = head.operations
      .map((o, i) => ({ i, name: (o as { op?: unknown } | null)?.op }))
      .filter((x) => !OPERATION_NAMES.includes(x.name as Operation['op']));
    if (unknown.length) {
      return fail(
        'schema',
        unknown.slice(0, 20).map((x) => ({ path: `operations[${x.i}].op`, message: `Unknown operation ${JSON.stringify(x.name)}. Supported: ${OPERATION_NAMES.join(', ')}.` })),
      );
    }
  }
  const parsed = PackageSchema.safeParse(raw);
  if (!parsed.success) return fail('schema', zodIssues(parsed.error));
  const pkg = parsed.data;
  const needsV2 = version2FieldsIn(pkg);
  if (needsV2.length) {
    return fail(
      'version',
      needsV2.slice(0, 20).map((path) => ({ path, message: `"${path.split('.').pop()}" is a Look v1 field and needs package version 2. Set "version": 2 (version 1 packages without it keep working).` })),
    );
  }
  const totalKeys = pkg.operations.reduce((n, o) => n + (o.op === 'animation.keyframes' ? o.keys.length : 0), 0);
  if (totalKeys > PACKAGE_LIMITS.keysTotal) return fail('size', [{ path: 'operations', message: `The package sets ${totalKeys} keyframes; the limit is ${PACKAGE_LIMITS.keysTotal}.` }]);

  // 3. Compatibility with this project.
  const warnings: string[] = [];
  if (pkg.target?.projectId && pkg.target.projectId !== project.id) {
    return fail('compatibility', [{ path: 'target.projectId', message: 'This package was written for a different project. Open that project, or remove "target.projectId" if the package only creates new content.' }]);
  }
  if (pkg.target?.schemaVersion !== undefined && pkg.target.schemaVersion > CURRENT_SCHEMA_VERSION) {
    return fail('compatibility', [{ path: 'target.schemaVersion', message: `The package needs project schema v${pkg.target.schemaVersion}; this SonicForge has v${CURRENT_SCHEMA_VERSION}.` }]);
  }
  if (pkg.target?.contextRevision) {
    const now = contextRevision(project);
    if (now !== pkg.target.contextRevision) {
      return fail('conflict', [
        {
          path: 'target.contextRevision',
          message: `The project has changed since its context was exported (package: ${pkg.target.contextRevision}, project now: ${now}). Export a fresh Project Context and ask the assistant to update the package.`,
        },
      ]);
    }
  }

  // 4. Apply every operation to the staged project, with deterministic ids.
  const copy = Math.max(0, Math.floor(options.copy ?? 0));
  const existing = allIds(project);
  let counter = 0;
  const ids = (prefix: string) => {
    const id = `${prefix || 'id'}_${hash24(`${pkg.id}|${copy}|${counter++}`)}`;
    if (existing.has(id)) throw new AlreadyApplied(id);
    return id;
  };
  const refs = new Map<string, { kind: 'scene' | 'layer' | 'object' | 'character' | 'animation' | 'session'; id: string; sceneId?: string }>();
  const summaries: OperationSummary[] = [];
  let focusSceneId: string | null = null;
  let next = project;
  try {
    withIdFactory(ids, () => {
      pkg.operations.forEach((operation, index) => {
        const path = `operations[${index}]`;
        try {
          const result = apply(next, operation, path, refs);
          next = result.project;
          if (result.focus) focusSceneId = result.focus;
          summaries.push({ index, op: operation.op, description: result.description });
        } catch (error) {
          if (error instanceof AlreadyApplied || error instanceof PackageOpError) throw error;
          throw new PackageOpError(path, error instanceof Error ? error.message : String(error));
        }
      });
    });
  } catch (error) {
    if (error instanceof AlreadyApplied) {
      return fail('already-applied', [
        { path: '', message: `This package has already been applied to this project (its content, e.g. id ${error.message}, already exists). Import it as a new copy if you want it twice.` },
      ]);
    }
    if (error instanceof PackageOpError) return fail('operation', [{ path: error.path, message: error.message }]);
    throw error;
  }

  // 5. Only creative (visual) content may change; then the full project validation.
  for (const key of Object.keys(next) as (keyof Project)[]) {
    if (!VISUAL_FIELDS.has(key) && next[key] !== project[key]) {
      return fail('validation', [{ path: '', message: `Internal safety check: the package would change "${key}", which packages may not touch.` }]);
    }
  }
  const valid = ProjectSchema.safeParse(next);
  if (!valid.success) {
    return fail('validation', formatIssues(valid.error).slice(0, 20).map((m) => ({ path: '', message: `The result would not be a valid project: ${m}` })));
  }
  next = valid.data;

  // 6. Preview.
  const diff = diffProjects(project, next);
  const destructive: string[] = [];
  for (const d of diff.deleted) destructive.push(`Removes ${d.kind} “${d.name}”${d.sceneName ? ` (scene “${d.sceneName}”)` : ''}.`);
  const touchedExisting = diff.modified.filter((m) => existing.has(m.id));
  if (touchedExisting.length) warnings.push(`Changes ${touchedExisting.length} existing item${touchedExisting.length === 1 ? '' : 's'} (listed under “Modified”).`);
  if (!pkg.target?.projectId && pkg.operations.some((o) => usesExistingIds(o))) {
    warnings.push('The package refers to existing items by id but does not name its target project; check that it was written for this project.');
  }
  if (pkg.target?.schemaVersion !== undefined && pkg.target.schemaVersion < CURRENT_SCHEMA_VERSION) {
    warnings.push(`Written for project schema v${pkg.target.schemaVersion}; this project is v${CURRENT_SCHEMA_VERSION}. The supported operations are unchanged.`);
  }
  if (copy > 0) warnings.push(`Imported as copy ${copy}: new items get fresh ids.`);
  const keyframesAdded = countKeys(next) - countKeys(project);
  return {
    ok: true,
    staged: {
      pkg,
      base: project,
      baseRevision: contextRevision(project),
      next,
      copy,
      operations: summaries,
      created: diff.created,
      modified: diff.modified,
      deleted: diff.deleted,
      keyframesAdded,
      warnings,
      destructive,
      focusSceneId,
    },
  };
}

const usesExistingIds = (o: Operation) => JSON.stringify(o).match(/"(scene|layer|object|character|animation|session)":"(?!@)[A-Za-z0-9_-]+"/) !== null;

const countKeys = (p: Project) => p.animations.reduce((n, a) => n + a.tracks.reduce((m, t) => m + t.keyframes.length, 0), 0);

// ------------------------------------------------------------------ operation handlers

type RefMap = Map<string, { kind: 'scene' | 'layer' | 'object' | 'character' | 'animation' | 'session'; id: string; sceneId?: string }>;
interface Applied {
  project: Project;
  description: string;
  focus?: string;
}

function declare(refs: RefMap, path: string, name: string, entry: { kind: 'scene' | 'layer' | 'object' | 'character' | 'animation' | 'session'; id: string; sceneId?: string }) {
  if (refs.has(name)) throw new PackageOpError(`${path}.ref`, `The reference name "${name}" is already used earlier in this package.`);
  refs.set(name, entry);
}

function resolve(refs: RefMap, path: string, ref: Ref, kind: 'scene' | 'layer' | 'object' | 'character' | 'animation' | 'session'): string {
  if (ref.startsWith('@')) {
    const entry = refs.get(ref.slice(1));
    if (!entry) throw new PackageOpError(path, `${ref} is not defined by an earlier operation in this package.`);
    if (entry.kind !== kind) throw new PackageOpError(path, `${ref} is a ${entry.kind}, not a ${kind}.`);
    return entry.id;
  }
  return ref;
}

const sceneId = (p: Project, refs: RefMap, path: string, ref: Ref) => {
  const id = resolve(refs, path, ref, 'scene');
  if (!p.scenes.some((s) => s.id === id)) throw new PackageOpError(path, `There is no scene "${id}" in this project.`);
  return id;
};

function objectIn(p: Project, refs: RefMap, path: string, ref: Ref, sid: string): SceneObject {
  const id = resolve(refs, path, ref, 'object');
  const scene = S.sceneOf(p, sid);
  const o = scene.objects.find((x) => x.id === id);
  if (!o) {
    const elsewhere = p.scenes.find((s) => s.objects.some((x) => x.id === id));
    throw new PackageOpError(path, elsewhere ? `Object "${id}" belongs to scene “${elsewhere.name}”, not “${scene.name}”.` : `There is no object "${id}" in scene “${scene.name}”.`);
  }
  return o;
}

function layerIn(p: Project, refs: RefMap, path: string, ref: Ref, sid: string): string {
  const id = resolve(refs, path, ref, 'layer');
  if (!S.sceneOf(p, sid).layers.some((l) => l.id === id)) throw new PackageOpError(path, `There is no layer "${id}" in this scene.`);
  return id;
}

/** Package object fields → the domain's object patch (styles nest under `style`). */
// ------------------------------------------------------------------ Look v1 (version 2)

const DEFAULT_GLOW = { radius: 16, strength: 0.8 };
const DEFAULT_SHADOW: Shadow = { color: '#000000', blur: 16, opacity: 0.45, offsetX: 0, offsetY: 10 };
const DEFAULT_VIGNETTE: Vignette = { amount: 0.5, softness: 0.5, color: '#000000' };

function gradientOf(spec: FillGradientSpec): FillGradient {
  const stops = spec.stops.map((s) => ({ offset: s.offset, color: s.color, opacity: s.opacity ?? 1 }));
  return spec.type === 'linear' ? { type: 'linear', stops, angle: spec.angle ?? 90 } : { type: 'radial', stops, cx: spec.cx ?? 0.5, cy: spec.cy ?? 0.5, radius: spec.radius ?? 0.5 };
}

/** The colour a new glow takes when the package leaves it out: the object's own colour. */
const ownColour = (o: SceneObject): string => ('style' in o ? o.style.fill : o.type === 'text' ? o.color : '#ffffff');

/**
 * Glow, shadow and gradient fields of an object spec or patch, resolved against the object
 * as it is now: effects merge with the current one (or the defaults), null removes them.
 */
function lookPatch(target: SceneObject, spec: { glow?: GlowSpec | null; shadow?: ShadowSpec | null; gradient?: FillGradientSpec | null }): S.ObjectPatch {
  const patch: Record<string, unknown> = {};
  if (spec.glow !== undefined) patch.glow = spec.glow === null ? null : ({ color: ownColour(target), ...DEFAULT_GLOW, ...(target.glow ?? {}), ...spec.glow } satisfies Glow);
  if (spec.shadow !== undefined) patch.shadow = spec.shadow === null ? null : ({ ...DEFAULT_SHADOW, ...(target.shadow ?? {}), ...spec.shadow } satisfies Shadow);
  if (spec.gradient !== undefined) patch.style = { gradient: spec.gradient === null ? null : gradientOf(spec.gradient) };
  return patch as S.ObjectPatch;
}

/** Applies a scene `look` spec: grade and vignette each merge with the current value; null removes. */
function applyLook(p: Project, sid: string, look: LookSpec): Project {
  const current: NonNullable<Scene['look']> = S.sceneOf(p, sid).look ?? {};
  const patch: Partial<NonNullable<Scene['look']>> = {};
  if (look.grade !== undefined) patch.grade = look.grade === null ? null : ({ ...NEUTRAL_GRADE, ...(current.grade ?? {}), ...(look.grade as GradeSpec) } satisfies Grade);
  if (look.vignette !== undefined) patch.vignette = look.vignette === null ? null : ({ ...DEFAULT_VIGNETTE, ...(current.vignette ?? {}), ...(look.vignette as VignetteSpec) } satisfies Vignette);
  return Object.keys(patch).length ? S.setSceneLook(p, sid, patch) : p;
}

function objectPatch(spec: Partial<ObjectSpec> | ObjectPatchSpec): S.ObjectPatch {
  const patch: Record<string, unknown> = {};
  const copy = ['name', 'x', 'y', 'width', 'height', 'rotation', 'opacity', 'visible', 'anchorX', 'anchorY', 'cornerRadius', 'points', 'text', 'fontSize', 'fontFamily', 'bold', 'align', 'color', 'fit', 'flipX'] as const;
  for (const key of copy) if (key in spec && (spec as Record<string, unknown>)[key] !== undefined) patch[key] = (spec as Record<string, unknown>)[key];
  const style: Record<string, unknown> = {};
  if ('fill' in spec && spec.fill !== undefined) style.fill = spec.fill;
  if ('stroke' in spec && spec.stroke !== undefined) style.stroke = spec.stroke;
  if (Object.keys(style).length) patch.style = style;
  return patch as S.ObjectPatch;
}

/** Fields each object type accepts (beyond the shared transform fields). */
const TYPE_FIELDS: Record<SceneObject['type'], readonly string[]> = {
  rect: ['fill', 'stroke', 'gradient', 'cornerRadius'],
  circle: ['fill', 'stroke', 'gradient'],
  ellipse: ['fill', 'stroke', 'gradient'],
  polygon: ['fill', 'stroke', 'gradient', 'points'],
  text: ['text', 'fontSize', 'fontFamily', 'bold', 'align', 'color'],
  image: ['fit'],
  character: ['flipX'],
};
const SHARED_FIELDS = new Set(['name', 'x', 'y', 'width', 'height', 'rotation', 'opacity', 'visible', 'locked', 'anchorX', 'anchorY', 'layer', 'glow', 'shadow']);

function characterPatch(name: string | undefined, appearance: AppearanceSpec | undefined, proportions: Record<string, number> | undefined): S.CharacterPatch {
  const patch: S.CharacterPatch = {};
  if (name) patch.name = name;
  if (appearance?.skin) patch.skin = appearance.skin;
  if (appearance?.head) patch.head = appearance.head;
  if (appearance?.eyes) patch.eyes = appearance.eyes;
  if (appearance?.hair) patch.hair = appearance.hair;
  if (appearance?.clothing) patch.clothing = appearance.clothing;
  if (appearance?.accessories) patch.accessories = appearance.accessories;
  if (proportions && Object.keys(proportions).length) patch.proportions = proportions;
  return patch;
}

function apply(p: Project, o: Operation, path: string, refs: RefMap): Applied {
  switch (o.op) {
    case 'scene.create': {
      const before = p.activeSceneId;
      let next = o.preset ? S.addScene(p, buildPresetScene(o.preset)) : S.addScene(p, undefined, o.name);
      const id = next.activeSceneId;
      if (o.preset) next = S.renameScene(next, id, o.name);
      if (o.width !== undefined || o.height !== undefined) {
        const s = S.sceneOf(next, id);
        next = S.setSceneSize(next, id, o.width ?? s.width, o.height ?? s.height);
      }
      if (o.background !== undefined || o.gradient !== undefined) next = S.setSceneBackground(next, id, { ...(o.background ? { background: o.background } : {}), ...(o.gradient !== undefined ? { gradient: o.gradient } : {}) });
      if (o.look) next = applyLook(next, id, o.look);
      if (o.activate === false) next = S.setActiveScene(next, before);
      declare(refs, path, o.ref, { kind: 'scene', id });
      return { project: next, description: `Create scene “${o.name}”${o.preset ? ` from the ${o.preset} environment` : ''}.`, focus: id };
    }
    case 'scene.update': {
      const id = sceneId(p, refs, `${path}.scene`, o.scene);
      let next = p;
      if (o.name) next = S.renameScene(next, id, o.name);
      if (o.width !== undefined || o.height !== undefined) {
        const s = S.sceneOf(next, id);
        next = S.setSceneSize(next, id, o.width ?? s.width, o.height ?? s.height);
      }
      if (o.background !== undefined || o.gradient !== undefined) next = S.setSceneBackground(next, id, { ...(o.background ? { background: o.background } : {}), ...(o.gradient !== undefined ? { gradient: o.gradient } : {}) });
      if (o.look) next = applyLook(next, id, o.look);
      if (o.activate) next = S.setActiveScene(next, id);
      return { project: next, description: `Update scene “${S.sceneOf(next, id).name}”.`, focus: id };
    }
    case 'scene.delete': {
      const id = sceneId(p, refs, `${path}.scene`, o.scene);
      const name = S.sceneOf(p, id).name;
      return { project: S.deleteScene(p, id), description: `Delete scene “${name}” with its objects, animations and sessions.` };
    }
    case 'layer.create': {
      const sid = sceneId(p, refs, `${path}.scene`, o.scene);
      const before = new Set(S.sceneOf(p, sid).layers.map((l) => l.id));
      let next = S.addLayer(p, sid, o.name);
      const layer = S.sceneOf(next, sid).layers.find((l) => !before.has(l.id))!;
      if (o.opacity !== undefined || o.blend !== undefined) next = S.updateLayer(next, sid, layer.id, { ...(o.opacity !== undefined ? { opacity: o.opacity } : {}), ...(o.blend ? { blend: o.blend } : {}) });
      declare(refs, path, o.ref, { kind: 'layer', id: layer.id, sceneId: sid });
      return { project: next, description: `Add layer “${o.name}” to “${S.sceneOf(next, sid).name}”.` };
    }
    case 'layer.update': {
      const sid = sceneId(p, refs, `${path}.scene`, o.scene);
      const lid = layerIn(p, refs, `${path}.layer`, o.layer, sid);
      const patch = {
        ...(o.name ? { name: o.name } : {}),
        ...(o.visible !== undefined ? { visible: o.visible } : {}),
        ...(o.locked !== undefined ? { locked: o.locked } : {}),
        ...(o.opacity !== undefined ? { opacity: o.opacity } : {}),
        ...(o.blend ? { blend: o.blend } : {}),
      };
      if (!Object.keys(patch).length) throw new PackageOpError(path, 'Set at least one of name, visible, locked, opacity or blend.');
      return { project: S.updateLayer(p, sid, lid, patch), description: `Update layer “${S.layerOf(S.sceneOf(p, sid), lid).name}”.` };
    }
    case 'character.create': {
      const before = new Set(p.characters.map((c) => c.id));
      let next = S.createCharacter(p, o.preset, undefined, o.name);
      const id = next.characters.find((c) => !before.has(c.id))!.id;
      const patch = characterPatch(undefined, o.appearance, o.proportions as Record<string, number> | undefined);
      if (Object.keys(patch).length) next = S.updateCharacter(next, id, patch);
      declare(refs, path, o.ref, { kind: 'character', id });
      return { project: next, description: `Create character “${S.characterOf(next, id).name}” from the ${o.preset} preset${Object.keys(patch).length ? ', customised' : ''}.` };
    }
    case 'character.update': {
      const id = resolve(refs, `${path}.character`, o.character, 'character');
      if (!p.characters.some((c) => c.id === id)) throw new PackageOpError(`${path}.character`, `There is no character "${id}" in this project.`);
      const patch = characterPatch(o.name, o.appearance, o.proportions as Record<string, number> | undefined);
      if (!Object.keys(patch).length) throw new PackageOpError(path, 'Set at least one of name, appearance or proportions.');
      return { project: S.updateCharacter(p, id, patch), description: `Update character “${S.characterOf(p, id).name}” (every instance changes).` };
    }
    case 'object.create': {
      const sid = sceneId(p, refs, `${path}.scene`, o.scene);
      const spec = o.object;
      const layerId = o.layer ? layerIn(p, refs, `${path}.layer`, o.layer, sid) : undefined;
      let newObject: S.NewObject;
      if (spec.type === 'image') {
        const asset = p.assets.find((a) => a.id === spec.asset);
        if (!asset) throw new PackageOpError(`${path}.object.asset`, `There is no asset "${spec.asset}" in this project. Packages can only use files already imported into the project.`);
        if (asset.kind !== 'image') throw new PackageOpError(`${path}.object.asset`, `Asset “${asset.name}” is ${asset.kind}, not an image.`);
        newObject = { type: 'image', assetId: asset.id };
      } else if (spec.type === 'character') {
        const cid = resolve(refs, `${path}.object.character`, spec.character, 'character');
        if (!p.characters.some((c) => c.id === cid)) throw new PackageOpError(`${path}.object.character`, `There is no character "${cid}" in this project.`);
        newObject = { type: 'character', characterId: cid };
      } else if (spec.type === 'text') newObject = { type: 'text', text: spec.text, color: spec.color };
      else if (spec.type === 'polygon') newObject = { type: 'polygon', fill: spec.fill, points: spec.points };
      else newObject = { type: spec.type, fill: spec.fill };
      const created = S.createObject(p, sid, newObject, { x: spec.x, y: spec.y, width: spec.width, height: spec.height, layerId });
      let next = created.project;
      const patch = objectPatch(spec);
      if (Object.keys(patch).length) next = S.updateObjects(next, sid, [created.id], patch);
      const look = lookPatch(S.objectOf(S.sceneOf(next, sid), created.id), spec);
      if (Object.keys(look).length) next = S.updateObjects(next, sid, [created.id], look);
      if (spec.locked) next = S.setLocked(next, sid, [created.id], true);
      declare(refs, path, o.ref, { kind: 'object', id: created.id, sceneId: sid });
      const made = S.objectOf(S.sceneOf(next, sid), created.id);
      return { project: next, description: `Add ${spec.type} “${made.name}” to “${S.sceneOf(next, sid).name}”.` };
    }
    case 'object.update': {
      const sid = sceneId(p, refs, `${path}.scene`, o.scene);
      const target = objectIn(p, refs, `${path}.object`, o.object, sid);
      const allowed = new Set([...SHARED_FIELDS, ...TYPE_FIELDS[target.type]]);
      for (const key of Object.keys(o.set)) {
        if (!allowed.has(key)) throw new PackageOpError(`${path}.set.${key}`, `A ${target.type} object has no "${key}" field.`);
      }
      let next = p;
      const { layer, locked, ...rest } = o.set;
      if (target.locked && locked !== false) throw new PackageOpError(`${path}.object`, `“${target.name}” is locked. Set "locked": false to edit it.`);
      if (target.locked) next = S.setLocked(next, sid, [target.id], false);
      const patch = objectPatch(rest);
      if (Object.keys(patch).length) next = S.updateObjects(next, sid, [target.id], patch);
      const look = lookPatch(S.objectOf(S.sceneOf(next, sid), target.id), rest);
      if (Object.keys(look).length) next = S.updateObjects(next, sid, [target.id], look);
      if (layer) next = S.moveObjectsToLayer(next, sid, [target.id], layerIn(next, refs, `${path}.set.layer`, layer, sid));
      if (locked) next = S.setLocked(next, sid, [target.id], true);
      return { project: next, description: `Update “${target.name}”: ${Object.keys(o.set).join(', ')}.` };
    }
    case 'object.delete': {
      const sid = sceneId(p, refs, `${path}.scene`, o.scene);
      const targets = o.objects.map((r, i) => objectIn(p, refs, `${path}.objects[${i}]`, r, sid));
      return { project: S.deleteObjects(p, sid, targets.map((t) => t.id)), description: `Delete ${targets.map((t) => `“${t.name}”`).join(', ')} (with their animation tracks and reactions).` };
    }
    case 'object.order': {
      const sid = sceneId(p, refs, `${path}.scene`, o.scene);
      const targets = o.objects.map((r, i) => objectIn(p, refs, `${path}.objects[${i}]`, r, sid));
      return { project: S.reorderObjects(p, sid, targets.map((t) => t.id), o.to), description: `Move ${targets.map((t) => `“${t.name}”`).join(', ')} ${o.to === 'front' || o.to === 'back' ? `to the ${o.to}` : o.to}.` };
    }
    case 'animation.create': {
      const sid = sceneId(p, refs, `${path}.scene`, o.scene);
      const created = A.createAnimation(p, sid, { name: o.name, seconds: o.seconds, fps: o.fps, loop: o.loop });
      declare(refs, path, o.ref, { kind: 'animation', id: created.id, sceneId: sid });
      const a = A.animationOf(created.project, created.id);
      return { project: created.project, description: `Create animation “${a.name}” (${o.seconds} s at ${a.fps} fps, ${a.loop}).` };
    }
    case 'animation.keyframes': {
      const aid = animationId(p, refs, `${path}.animation`, o.animation);
      const a = A.animationOf(p, aid);
      const target = objectIn(p, refs, `${path}.object`, o.object, a.sceneId);
      // Merged into the existing track and written with one validated replaceTracks call
      // (setKeyframe per key re-validates the whole animation each time: quadratic).
      // Same rules as setKeyframe: a key on an existing frame replaces it and keeps its
      // easing unless one is given.
      const track = a.tracks.find((t) => t.objectId === target.id && t.channel === o.channel);
      const merged = new Map((track?.keyframes ?? []).map((k) => [k.frame, k]));
      const seen = new Set<number>();
      o.keys.forEach((k, i) => {
        const frame = Math.round(k.time * a.fps);
        if (frame >= a.durationFrames) throw new PackageOpError(`${path}.keys[${i}].time`, `${k.time} s is past the end of “${a.name}” (${(a.durationFrames / a.fps).toFixed(2)} s).`);
        if (seen.has(frame)) throw new PackageOpError(`${path}.keys[${i}].time`, `Two keys land on frame ${frame} (${k.time} s at ${a.fps} fps).`);
        seen.add(frame);
        merged.set(frame, { frame, value: k.value, easing: k.easing ?? merged.get(frame)?.easing ?? A.DEFAULT_EASING });
      });
      const keyframes = [...merged.values()].sort((x, y) => x.frame - y.frame);
      const next = A.replaceTracks(p, aid, target.id, { [o.channel]: keyframes });
      return { project: next, description: `Key “${target.name}” ${o.channel} at ${o.keys.length} time${o.keys.length === 1 ? '' : 's'} in “${a.name}”.` };
    }
    case 'animation.preset': {
      const aid = animationId(p, refs, `${path}.animation`, o.animation);
      const a = A.animationOf(p, aid);
      const target = objectIn(p, refs, `${path}.object`, o.object, a.sceneId);
      return { project: A.applyMotionPreset(p, aid, target.id, o.preset), description: `Apply the ${o.preset} motion preset to “${target.name}” in “${a.name}”.` };
    }
    case 'animation.motion': {
      const aid = animationId(p, refs, `${path}.animation`, o.animation);
      const a = A.animationOf(p, aid);
      const binding: Record<string, string> = {};
      const slots = o.slots.map((s, i) => {
        const target = objectIn(p, refs, `${path}.slots[${i}].object`, s.object, a.sceneId);
        binding[s.name] = target.id;
        return { name: s.name, kind: target.type === 'character' ? ('character' as const) : ('object' as const), label: target.name };
      });
      const result = applyMotionPlan(p, {
        plan: { version: MOTION_PLAN_VERSION, seed: o.seed ?? 1, totalSeconds: o.totalSeconds ?? null, slots, steps: o.steps as never },
        binding,
        policy: o.policy ?? 'merge',
        sceneId: a.sceneId,
        animationId: aid,
        allowExtend: o.allowExtend ?? false,
      });
      const names = [...new Set(o.steps.map((s) => s.template))].join(', ');
      return { project: result.project, description: `Compose motion (${names}) in “${a.name}”${result.replacedKeys ? `, replacing ${result.replacedKeys} keys` : ''}.` };
    }
    case 'sync.session': {
      const sid = sceneId(p, refs, `${path}.scene`, o.scene);
      const anim = o.animation ? animationId(p, refs, `${path}.animation`, o.animation) : null;
      if (anim && A.animationOf(p, anim).sceneId !== sid) throw new PackageOpError(`${path}.animation`, 'The animation belongs to a different scene.');
      const created = av.createSession(p, { name: o.name, sceneId: sid, animationId: anim });
      declare(refs, path, o.ref, { kind: 'session', id: created.id });
      return { project: created.project, description: `Create sync session “${av.sessionOf(created.project, created.id).name}” on “${S.sceneOf(p, sid).name}”.` };
    }
    case 'sync.reaction': {
      const sessionRef = resolve(refs, `${path}.session`, o.session, 'session');
      if (!p.avSessions.some((s) => s.id === sessionRef)) throw new PackageOpError(`${path}.session`, `There is no sync session "${sessionRef}" in this project.`);
      const s = av.sessionOf(p, sessionRef);
      const sceneReaction = (SCENE_REACTIONS as readonly string[]).includes(o.reaction);
      if (sceneReaction && o.object) throw new PackageOpError(`${path}.object`, `A ${o.reaction} reaction affects the whole scene; remove "object".`);
      if (!sceneReaction && !o.object) throw new PackageOpError(`${path}.object`, `A ${o.reaction} reaction needs an "object".`);
      const target = o.object ? objectIn(p, refs, `${path}.object`, o.object, s.sceneId) : null;
      const base = av.defaultMapping(o.reaction, o.categories, target?.id ?? null);
      const mapping = {
        ...base,
        ...(o.name ? { name: o.name } : {}),
        source: { ...base.source, ...(o.every !== undefined ? { every: o.every } : {}) },
        ...(o.offsetBeats !== undefined ? { offsetBeats: o.offsetBeats } : {}),
      };
      return { project: av.addMapping(p, s.id, mapping), description: `React with ${o.reaction} on ${o.categories.join(', ')}${target ? ` (“${target.name}”)` : ''} in “${s.name}”.` };
    }
    case 'visualizer.preset': {
      const sessionRef = resolve(refs, `${path}.session`, o.session, 'session');
      if (!p.avSessions.some((s) => s.id === sessionRef)) throw new PackageOpError(`${path}.session`, `There is no sync session "${sessionRef}" in this project.`);
      const result = viz.applyPreset(p, o.preset, { sessionId: sessionRef });
      return { project: result.project, description: `Apply the ${o.preset} visualizer preset to “${av.sessionOf(p, sessionRef).name}” (replaces its visualizer layers).` };
    }
  }
}

function animationId(p: Project, refs: RefMap, path: string, ref: Ref): string {
  const id = resolve(refs, path, ref, 'animation');
  if (!p.animations.some((a) => a.id === id)) throw new PackageOpError(path, `There is no animation "${id}" in this project.`);
  return id;
}

// ------------------------------------------------------------------ preview diff

function diffProjects(before: Project, after: Project): { created: EntityChange[]; modified: EntityChange[]; deleted: EntityChange[] } {
  const created: EntityChange[] = [];
  const modified: EntityChange[] = [];
  const deleted: EntityChange[] = [];
  const compare = (kind: EntityKind, a: Map<string, { name: string; json: string; sceneName?: string }>, b: Map<string, { name: string; json: string; sceneName?: string }>) => {
    for (const [id, x] of b) {
      const old = a.get(id);
      if (!old) created.push({ kind, id, name: x.name, sceneName: x.sceneName });
      else if (old.json !== x.json) modified.push({ kind, id, name: x.name, sceneName: x.sceneName });
    }
    for (const [id, x] of a) if (!b.has(id)) deleted.push({ kind, id, name: x.name, sceneName: x.sceneName });
  };
  const index = (p: Project) => {
    const scenes = new Map<string, { name: string; json: string }>();
    const layers = new Map<string, { name: string; json: string; sceneName: string }>();
    const objects = new Map<string, { name: string; json: string; sceneName: string }>();
    for (const s of p.scenes) {
      const { objects: objs, layers: lyrs, ...rest } = s;
      scenes.set(s.id, { name: s.name, json: JSON.stringify(rest) });
      for (const l of lyrs) layers.set(`${s.id}/${l.id}`, { name: l.name, json: JSON.stringify(l), sceneName: s.name });
      for (const o of objs) objects.set(o.id, { name: o.name, json: JSON.stringify(o), sceneName: s.name });
    }
    const simple = <T extends { id: string; name: string }>(list: readonly T[]) => new Map(list.map((x) => [x.id, { name: x.name, json: JSON.stringify(x) }]));
    const reactions = new Map<string, { name: string; json: string }>();
    const vizLayers = new Map<string, { name: string; json: string }>();
    for (const s of p.avSessions) {
      for (const m of s.mappings) reactions.set(m.id, { name: m.name, json: JSON.stringify(m) });
      for (const l of s.visualizer.layers) vizLayers.set(l.id, { name: l.name, json: JSON.stringify(l) });
    }
    const sessions = new Map(p.avSessions.map((s) => {
      const { mappings, visualizer, ...rest } = s;
      void mappings;
      void visualizer;
      return [s.id, { name: s.name, json: JSON.stringify(rest) }];
    }));
    return { scenes, layers, objects, characters: simple(p.characters), animations: simple(p.animations), sessions, reactions, vizLayers };
  };
  const a = index(before);
  const b = index(after);
  compare('scene', a.scenes, b.scenes);
  compare('layer', a.layers, b.layers);
  compare('object', a.objects, b.objects);
  compare('character', a.characters, b.characters);
  compare('animation', a.animations, b.animations);
  compare('session', a.sessions, b.sessions);
  compare('reaction', a.reactions, b.reactions);
  compare('visualizer layer', a.vizLayers, b.vizLayers);
  // Layers are keyed scene/layer for uniqueness; report their own id.
  for (const list of [created, modified, deleted]) for (const e of list) if (e.kind === 'layer') e.id = e.id.split('/')[1]!;
  return { created, modified, deleted };
}

/**
 * The project with the staged package applied, or null when the creative content changed
 * after the preview (the preview must then be repeated). Only the creative fields the
 * package may change are taken from the staged copy, so anything else that changed in the
 * meantime (a save's timestamp, music edits) is kept.
 */
export function commitStaged(current: Project, staged: StagedPackage): Project | null {
  if (current !== staged.base && contextRevision(current) !== staged.baseRevision) return null;
  const { scenes, activeSceneId, characters, animations, avSessions } = staged.next;
  return { ...current, scenes, activeSceneId, characters, animations, avSessions };
}

export { DESTRUCTIVE_OPERATIONS };
